import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Button,
  Step,
  StepContent,
  StepIcon,
  StepLabel,
  Stepper,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import patientTemplateSample from "../../../../assets/admin/provider-group/patientTemplateSample.svg";
import { Option } from "../../../../constants/common";
import { useLoadingContext } from "../../../../context/LoadingProvider";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { usePgAddPatients } from "../../../../queries/use-add-pg-patient";
import {
  DownloadTemplateTypeEnum,
  MigrationControllerApi,
} from "../../../../services/api";
import { getConfigObject, getErrorSnackbar } from "../../../../utils/utils";
import Flex from "../../../Flex";
import SideSheetDrawer from "../../../SidesheetDrawer";
import DropZone from "../../DropZone";

interface StaffUserModalProps {
  open: boolean;
  modalTitle: string;
  onClose: () => void;
  hidePreviewStep?: boolean;
  //TODO need  to remove this
  //on save values may be template file
}

const AddPatient = ({
  open,
  modalTitle,
  onClose,
  hidePreviewStep,
}: StaffUserModalProps) => {
  const formik = useFormik<{ file: File | null }>({
    initialValues: { file: null },
    validationSchema: Yup.object({
      file: Yup.mixed()
        .required("File is required")
        .test(
          "fileType",
          "Unsupported file format. Please upload a valid file.",
          (value: any) => {
            if (!value) return false;
            const supportedFormats = [
              "text/csv", // .csv
              "application/vnd.ms-excel", // .xls
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
            ];
            return supportedFormats.includes(value.type);
          }
        ),
    }).label("File"),
    onSubmit: () => {
      onAddPatients();
    },
  });
  const { mutateAsync: addPatients } = usePgAddPatients();
  const params = useParams();
  const { setIsLoading } = useLoadingContext();
  const client = useQueryClient();
  const showSnackbar = useSnackbar();
  function getCSVTemplate(type: DownloadTemplateTypeEnum) {
    new MigrationControllerApi(getConfigObject())
      .downloadTemplate(
        "PATIENT",
        type,
        undefined, // xTENANTID
        { responseType: "blob" }
      )
      .then((res) => {
        const blobFile = res?.data;

        const fileUrl = URL.createObjectURL(blobFile);

        const fileName = `Patient data.${type === "XLSX" ? " xl" : ""}.${
          type === "XLSX" ? "xlsx" : "csv"
        }`;

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(fileUrl);
      });
  }

  async function onAddPatients() {
    setIsLoading(true);
    if (formik.values?.file)
      return addPatients({
        category: "PATIENT",
        title: formik?.values?.file?.name ?? "",
        file: formik.values?.file,
        providerGroupUuid: params?.uuid,
      })
        .then(() => {
          showSnackbar({
            type: "success",
            message: "Patients Added Successfully!",
          });
          setTimeout(() => {
            client.invalidateQueries({
              queryKey: ["provider-group-patients-list"],
            });
            onClose();
          }, 0);
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        })
        .finally(() => {
          setIsLoading(false);
        });
  }

  const stepsData: Option[] = [
    {
      value: "download",
      label: "Download CSV / XLSX Template",
      component: (
        <Flex gap={1}>
          {/* <a
            href="/Patient data.csv"
            download
            style={{ textDecoration: "none" }}
          >
            <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />}>
              Download Sample CSV Template
            </Button>
          </a> */}
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => getCSVTemplate("CSV")}
          >
            Download Sample CSV Template
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => getCSVTemplate("XLSX")}
          >
            Download Sample XLSX Template
          </Button>
        </Flex>
      ),
    },
    {
      value: "preview",
      label: "Add or Edit info in CSV / XLSX Template",
      component: (
        <img
          style={{ width: "600px" }}
          src={patientTemplateSample}
          alt="sample-patient-template"
          width={150}
        />
      ),
    },
    {
      value: "upload",
      label: "Upload CSV / XLSX File",
      component: (
        <Box width={"60%"}>
          <DropZone
            height={"121px"}
            accept={[".csv", ".xlsx"]?.join(",")}
            content="Browse CSV or XLSX File"
            onClick={(file) => formik.setFieldValue("file", file?.item(0))}
            error={formik.touched.file && formik.errors.file}
          />
        </Box>
      ),
    },
  ];

  return (
    <SideSheetDrawer
      onClose={onClose}
      open={open}
      title={modalTitle}
      footerActions={
        <Button onClick={formik.submitForm} variant="contained">
          Upload
        </Button>
      }
    >
      <Stepper orientation="vertical" sx={{ width: "100%" }}>
        {(hidePreviewStep
          ? stepsData?.filter((it) => it?.value !== "preview")
          : stepsData
        )?.map((step) => (
          <Step key={step.value} active expanded>
            <StepLabel
              StepIconComponent={(props) => (
                <StepIcon
                  {...props}
                  active={props.active || props.completed}
                  completed={false}
                />
              )}
            >
              {step.label}
            </StepLabel>
            <StepContent
              // Todo need to call api as per item click
              onClick={() => {}}
            >
              {step.component}
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </SideSheetDrawer>
  );
};

export default AddPatient;
